import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { showingbookerAPI } from '../../../services/showingbookerAPI';

const CalendarContainer = styled.div`
  padding: 20px 0;
  max-width: 1200px;
  margin: 0 auto;
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  h2 {
    margin: 0;
    color: #333;
    font-size: 28px;
    font-weight: 700;
  }
  
  .subtitle {
    font-size: 16px;
    color: #666;
    margin-top: 4px;
  }
`;

const CalendarControls = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const NavButton = styled.button`
  background: white;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 8px 12px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.2s ease;
  
  &:hover {
    background: #f8f9fa;
    border-color: #adb5bd;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TodayButton = styled.button`
  background: #007AFF;
  color: white;
  border: none;
  border-radius: 6px;
  padding: 8px 16px;
  cursor: pointer;
  font-weight: 500;
  transition: background 0.2s ease;
  
  &:hover {
    background: #0056b3;
  }
`;

const ActionButton = styled.button`
  padding: 8px 16px;
  border: 1px solid ${props => props.variant === 'sync' ? '#28a745' : '#007AFF'};
  background: ${props => props.variant === 'sync' ? '#28a745' : '#007AFF'};
  color: white;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 5px;
  
  &:hover {
    background: ${props => props.variant === 'sync' ? '#218838' : '#0056b3'};
    border-color: ${props => props.variant === 'sync' ? '#1e7e34' : '#004494'};
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const WeeksContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  
  @media (max-width: 768px) {
    gap: 20px;
  }
`;

const WeekContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.1);
  overflow: hidden;
`;

const WeekHeader = styled.div`
  background: #f8f9fa;
  padding: 16px 20px;
  border-bottom: 1px solid #dee2e6;
  
  h3 {
    margin: 0;
    color: #333;
    font-size: 18px;
    font-weight: 600;
  }
  
  .date-range {
    font-size: 14px;
    color: #666;
    margin-top: 2px;
  }
`;

const WeekGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DayColumn = styled.div`
  border-right: 1px solid #e9ecef;
  min-height: 200px;
  
  &:last-child {
    border-right: none;
  }
  
  @media (max-width: 768px) {
    border-right: none;
    border-bottom: 1px solid #e9ecef;
    min-height: auto;
    
    &:last-child {
      border-bottom: none;
    }
  }
`;

const DayHeader = styled.div`
  padding: 12px 16px;
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  text-align: center;
  
  .day-name {
    font-size: 12px;
    color: #666;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    font-weight: 500;
  }
  
  .day-number {
    font-size: 18px;
    font-weight: 600;
    color: ${props => props.isToday ? '#007AFF' : '#333'};
    margin-top: 4px;
  }
  
  &.today {
    background: #e3f2fd;
  }
`;

const DayContent = styled.div`
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ShowingCard = styled.div`
  background: ${props => props.status === 'confirmed' ? '#d4edda' : props.status === 'pending' ? '#fff3cd' : '#f8d7da'};
  border: 1px solid ${props => props.status === 'confirmed' ? '#c3e6cb' : props.status === 'pending' ? '#ffeaa7' : '#f5c6cb'};
  border-radius: 6px;
  padding: 8px 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
  
  &:hover .export-button {
    opacity: 1;
  }
  
  .time {
    font-size: 12px;
    font-weight: 600;
    color: ${props => props.status === 'confirmed' ? '#155724' : props.status === 'pending' ? '#856404' : '#721c24'};
    margin-bottom: 4px;
  }
  
  .address {
    font-size: 11px;
    color: #666;
    line-height: 1.3;
  }
  
  .buyer {
    font-size: 10px;
    color: #888;
    margin-top: 4px;
  }
`;

const ExportButton = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 2px 6px;
  font-size: 10px;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.2s ease;
  
  &:hover {
    background: white;
    border-color: #007AFF;
    color: #007AFF;
  }
`;

const EmptyDay = styled.div`
  padding: 20px 12px;
  text-align: center;
  color: #999;
  font-size: 14px;
  font-style: italic;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #666;
  font-size: 16px;
`;

const IntegrationNote = styled.div`
  background: #e3f2fd;
  border: 1px solid #bbdefb;
  border-radius: 8px;
  padding: 16px;
  margin-top: 20px;
  
  .title {
    font-weight: 600;
    color: #1976d2;
    margin-bottom: 8px;
  }
  
  .description {
    font-size: 14px;
    color: #666;
    margin-bottom: 12px;
  }
  
  .buttons {
    display: flex;
    gap: 10px;
  }
  
  button {
    background: #1976d2;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 8px 16px;
    cursor: pointer;
    font-size: 12px;
    font-weight: 500;
    
    &:hover {
      background: #1565c0;
    }
    
    &.secondary {
      background: #666;
      
      &:hover {
        background: #555;
      }
    }
  }
`;

const Calendar = () => {
  const [showings, setShowings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [currentStartDate, setCurrentStartDate] = useState(getCurrentWeekMonday());
  
  useEffect(() => {
    loadShowings();
  }, [currentStartDate]);

  function getCurrentWeekMonday() {
    const today = new Date();
    const day = today.getDay();
    // Calculate days back to Monday (0=Sunday, 1=Monday, 2=Tuesday, etc.)
    const daysBackToMonday = day === 0 ? 6 : day - 1; // If Sunday (0), go back 6 days; otherwise go back (day-1) days
    const monday = new Date(today);
    monday.setDate(today.getDate() - daysBackToMonday);
    monday.setHours(0, 0, 0, 0);
    return monday;
  }

  function formatDateRange(startDate, endDate) {
    const options = { month: 'short', day: 'numeric' };
    const start = startDate.toLocaleDateString('en-US', options);
    const end = endDate.toLocaleDateString('en-US', options);
    const year = startDate.getFullYear();
    return `${start} - ${end}, ${year}`;
  }

  function isToday(date) {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  }

  function navigateWeeks(direction) {
    const newDate = new Date(currentStartDate);
    newDate.setDate(currentStartDate.getDate() + (direction * 28));
    setCurrentStartDate(newDate);
  }

  function goToToday() {
    setCurrentStartDate(getNextMonday());
  }

  function generateICSFile(showing) {
    // Parse the showing date and time
    const showingDateTime = new Date(`${showing.showing_date}T${showing.showing_time}`);
    const endDateTime = new Date(showingDateTime.getTime() + (60 * 60 * 1000)); // Add 1 hour
    
    // Format dates for ICS (YYYYMMDDTHHMMSSZ)
    const formatICSDate = (date) => {
      return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    };
    
    const startDate = formatICSDate(showingDateTime);
    const endDate = formatICSDate(endDateTime);
    
    // Generate unique ID
    const uid = `showing-${showing.id}-${Date.now()}@showingbooker.com`;
    
    // Create ICS content
    const icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//ShowingBooker//ShowingBooker Calendar//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH
BEGIN:VEVENT
UID:${uid}
DTSTART:${startDate}
DTEND:${endDate}
DTSTAMP:${formatICSDate(new Date())}
SUMMARY:Property Showing: ${showing.property_address}
DESCRIPTION:Showing Details:\\n\\nProperty: ${showing.property_address}\\nBuyer: ${showing.buyer_name}\\nStatus: ${showing.status}\\n\\nGenerated by ShowingBooker
LOCATION:${showing.property_address}
STATUS:${showing.status === 'confirmed' ? 'CONFIRMED' : 'TENTATIVE'}
CATEGORIES:Property Showing,Real Estate
BEGIN:VALARM
TRIGGER:-PT15M
ACTION:DISPLAY
DESCRIPTION:Property showing in 15 minutes
END:VALARM
END:VEVENT
END:VCALENDAR`;

    // Create and download the file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `showing-${showing.property_address.replace(/[^a-zA-Z0-9]/g, '-')}-${showing.showing_date}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  function exportAllShowings() {
    if (showings.length === 0) {
      alert('No showings to export');
      return;
    }

    // Create ICS content for all showings
    let icsContent = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//ShowingBooker//ShowingBooker Calendar//EN
CALSCALE:GREGORIAN
METHOD:PUBLISH`;

    showings.forEach(showing => {
      const showingDateTime = new Date(`${showing.showing_date}T${showing.showing_time}`);
      const endDateTime = new Date(showingDateTime.getTime() + (60 * 60 * 1000));
      
      const formatICSDate = (date) => {
        return date.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
      };
      
      const startDate = formatICSDate(showingDateTime);
      const endDate = formatICSDate(endDateTime);
      const uid = `showing-${showing.id}-${Date.now()}@showingbooker.com`;
      
      icsContent += `
BEGIN:VEVENT
UID:${uid}
DTSTART:${startDate}
DTEND:${endDate}
DTSTAMP:${formatICSDate(new Date())}
SUMMARY:Property Showing: ${showing.property_address}
DESCRIPTION:Showing Details:\\n\\nProperty: ${showing.property_address}\\nBuyer: ${showing.buyer_name}\\nStatus: ${showing.status}\\n\\nGenerated by ShowingBooker
LOCATION:${showing.property_address}
STATUS:${showing.status === 'confirmed' ? 'CONFIRMED' : 'TENTATIVE'}
CATEGORIES:Property Showing,Real Estate
BEGIN:VALARM
TRIGGER:-PT15M
ACTION:DISPLAY
DESCRIPTION:Property showing in 15 minutes
END:VALARM
END:VEVENT`;
    });

    icsContent += `
END:VCALENDAR`;

    // Create and download the file
    const blob = new Blob([icsContent], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `showingbooker-calendar-${currentStartDate.toISOString().split('T')[0]}.ics`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  const loadShowings = async () => {
    try {
      setLoading(true);
      // Calculate 4-week date range
      const endDate = new Date(currentStartDate);
      endDate.setDate(currentStartDate.getDate() + 27); // 28 days total
      
      // Format dates for API call
      const startDateStr = currentStartDate.toISOString().split('T')[0];
      const endDateStr = endDate.toISOString().split('T')[0];
      
      // Fetch real calendar data
      const calendarData = await showingbookerAPI.getCalendarData(startDateStr, endDateStr);
      
      // Transform API data to match component expectations
      const transformedShowings = calendarData.map(showing => ({
        id: showing.id,
        property_address: showing.property_address,
        showing_date: showing.showing_date.split('T')[0], // Convert to YYYY-MM-DD format
        showing_time: showing.showing_time,
        buyer_name: showing.buyer_name,
        status: showing.status
      }));
      
      setShowings(transformedShowings);
    } catch (error) {
      console.error('Error loading calendar showings:', error);
      setShowings([]);
    } finally {
      setLoading(false);
    }
  };

  const syncFromBookings = async () => {
    try {
      setSyncing(true);
      console.log('🔄 Syncing calendar data from bookings...');
      
      // Refresh the calendar data by reloading from API
      await loadShowings();
      
      console.log('✅ Successfully synced calendar data');
    } catch (error) {
      console.error('❌ Error syncing calendar data:', error);
    } finally {
      setSyncing(false);
    }
  };

  const getShowingsForDate = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return showings.filter(showing => showing.showing_date === dateStr);
  };

  const renderWeek = (weekStart, weekNumber) => {
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekStart.getDate() + 6);
    
    const days = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(weekStart);
      date.setDate(weekStart.getDate() + i);
      days.push(date);
    }

    return (
      <WeekContainer key={weekNumber}>
        <WeekHeader>
          <h3>Week {weekNumber}</h3>
          <div className="date-range">
            {formatDateRange(weekStart, weekEnd)}
          </div>
        </WeekHeader>
        <WeekGrid>
          {days.map((date, index) => {
            const dayShowings = getShowingsForDate(date);
            const dayNames = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
            
            return (
              <DayColumn key={index}>
                <DayHeader isToday={isToday(date)} className={isToday(date) ? 'today' : ''}>
                  <div className="day-name">{dayNames[index]}</div>
                  <div className="day-number">{date.getDate()}</div>
                </DayHeader>
                <DayContent>
                  {dayShowings.length > 0 ? (
                    dayShowings.map(showing => (
                      <ShowingCard key={showing.id} status={showing.status}>
                        <ExportButton 
                          className="export-button"
                          onClick={(e) => {
                            e.stopPropagation();
                            generateICSFile(showing);
                          }}
                          title="Export to calendar"
                        >
                          📥
                        </ExportButton>
                        <div className="time">
                          {new Date(`${showing.showing_date}T${showing.showing_time}`).toLocaleTimeString('en-US', {
                            hour: 'numeric',
                            minute: '2-digit',
                            hour12: true
                          })}
                        </div>
                        <div className="address">{showing.property_address}</div>
                        <div className="buyer">{showing.buyer_name}</div>
                      </ShowingCard>
                    ))
                  ) : (
                    <EmptyDay>No showings</EmptyDay>
                  )}
                </DayContent>
              </DayColumn>
            );
          })}
        </WeekGrid>
      </WeekContainer>
    );
  };

  if (loading) {
    return (
      <CalendarContainer>
        <LoadingState>Loading your calendar...</LoadingState>
      </CalendarContainer>
    );
  }

  const week1Start = currentStartDate;
  const week2Start = new Date(currentStartDate);
  week2Start.setDate(currentStartDate.getDate() + 7);
  const week3Start = new Date(currentStartDate);
  week3Start.setDate(currentStartDate.getDate() + 14);
  const week4Start = new Date(currentStartDate);
  week4Start.setDate(currentStartDate.getDate() + 21);

  return (
    <CalendarContainer>
      <CalendarHeader>
        <div>
          <h2>📅 Showing Calendar</h2>
          <div className="subtitle">4-week schedule view</div>
        </div>
        <CalendarControls>
          <ActionButton
            variant="sync"
            onClick={syncFromBookings}
            disabled={syncing || loading}
            title="Refresh calendar data from bookings database"
          >
            {syncing ? '⏳ Syncing...' : '🔄 Sync from Bookings'}
          </ActionButton>
          <NavButton onClick={() => navigateWeeks(-1)}>
            ← Previous 4 Weeks
          </NavButton>
          <TodayButton onClick={goToToday}>
            Today
          </TodayButton>
          <NavButton onClick={() => navigateWeeks(1)}>
            Next 4 Weeks →
          </NavButton>
          <NavButton onClick={exportAllShowings}>
            📥 Export All (.ics)
          </NavButton>
        </CalendarControls>
      </CalendarHeader>

      <WeeksContainer>
        {renderWeek(week1Start, 1)}
        {renderWeek(week2Start, 2)}
        {renderWeek(week3Start, 3)}
        {renderWeek(week4Start, 4)}
      </WeeksContainer>

      {/* Calendar Integration Note */}
      <IntegrationNote>
        <div className="title">📱 Calendar Integration</div>
        <div className="description">
          Export individual showings or your entire schedule to any calendar app. The .ics files work with Google Calendar, Outlook, Apple Calendar, and more.
        </div>
        <div className="buttons">
          <button onClick={exportAllShowings}>
            📥 Export All Showings (.ics)
          </button>
          <button 
            className="secondary"
            onClick={() => window.open('https://support.google.com/calendar/answer/37118', '_blank')}
          >
            📖 How to Import to Google Calendar
          </button>
        </div>
      </IntegrationNote>
    </CalendarContainer>
  );
};

export default Calendar;